/**
 * Task 5.12 — the SSE consumer, extracted out of ChatView.tsx into a pure
 * module with no React and no fetch: it takes a `ReadableStream<Uint8Array>`
 * (and an optional `AbortSignal`) and yields typed events. This is what
 * makes all six F8 bugs testable with a synthetic stream and no browser —
 * before this extraction they were only verifiable by hand.
 */

/** A generic SSE frame parser: any "data: {...}" line's JSON, or null for
 * a heartbeat/comment line (no "data:" prefix at all — e.g. ": ping") or a
 * malformed frame. Comment lines are silently skipped here, never yielded
 * as a null event — `test_comment_lines_ignored`. */
function parseFrame<T>(raw: string): T | null {
  const lines = raw.split("\n");
  let dataStr = "";
  for (const line of lines) {
    const t = line.trim();
    if (t.startsWith("data:")) dataStr += t.slice(5).trim();
  }
  if (!dataStr) return null;
  try {
    return JSON.parse(dataStr) as T;
  } catch {
    return null;
  }
}

/**
 * Consumes an SSE `ReadableStream` and yields one parsed event per frame.
 *
 * - Aborting `signal` cancels the underlying reader (F8-1: Stop must
 *   actually stop consumption, not just stop *looking* at further events).
 * - The `TextDecoder` is flushed with a final no-arg `decode()` call after
 *   the loop ends (F8-5: `{stream: true}` buffers an incomplete multi-byte
 *   UTF-8 sequence across chunks; without the flush, a character split
 *   across a chunk boundary is silently dropped instead of decoded).
 * - A trailing frame with no closing blank line (the stream ends mid-frame)
 *   is still parsed from whatever is left in `buffer`.
 */
export async function* parseAnswerStream<T extends { type: string }>(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal
): AsyncGenerator<T, void, unknown> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const onAbort = () => {
    reader.cancel().catch(() => {});
  };
  if (signal) {
    if (signal.aborted) onAbort();
    else signal.addEventListener("abort", onAbort);
  }

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() || "";
      for (const raw of frames) {
        const evt = parseFrame<T>(raw);
        if (evt) yield evt;
      }
    }
    if (!signal?.aborted) {
      buffer += decoder.decode(); // flush any pending multi-byte tail
      if (buffer.trim()) {
        const evt = parseFrame<T>(buffer);
        if (evt) yield evt;
      }
    }
  } finally {
    if (signal) signal.removeEventListener("abort", onAbort);
    try {
      reader.releaseLock();
    } catch {
      // already released by cancel()
    }
  }
}
